// externals
import { RSAA } from 'redux-api-middleware';

// libraries
import type { SimpleFSA } from '@makemydeal/dr-browser-utils';
import type { RSAACallAction, RSAACallRequest } from '@makemydeal/dr-react-18-wrappers';
import {
    apiConfigHelper,
    locHrefUtil,
    offerSessionUtils,
    getDocumentReferrer,
    DEAL_EXCHANGE_ID,
    HostEnvironment
} from '@makemydeal/dr-shared-ui-utils';

// types
import { bootstrapActionTypes, applicationLoadErrorActionTypes } from '@makemydeal/dr-shared-store';

export type BootstrapAppAction = SimpleFSA<typeof bootstrapActionTypes.BOOTSTRAP_REQUEST>;

export type BootstrapInitialLoadAction = SimpleFSA<typeof bootstrapActionTypes.BOOTSTRAP_INITIAL_LOAD>;

export const initialLoad = (): BootstrapInitialLoadAction => ({ type: bootstrapActionTypes.BOOTSTRAP_INITIAL_LOAD });

export const fetchBootstrapData = (appPrefix: string, environment = HostEnvironment.None, coOrgId = '') => {
    return (dispatch: any) => {
        dispatch(fetchBootstrapDataWorker(appPrefix, environment, coOrgId));
    };
};

export const fetchBootstrapDataWorker = (appPrefix: string, environment = HostEnvironment.None, coOrgId = '') => {
    // get the endpoint from the domain called
    let endpoint: string;

    const getBffApiUrl = (appPrefix: string, environment: HostEnvironment) => {
        if (environment !== HostEnvironment.None) {
            const builder = apiConfigHelper.apiUrlBuilderFromEnvironment(environment, appPrefix);
            return builder.buildBffApiUrl('/init');
        }
        return apiConfigHelper.buildBffApiUrl(appPrefix, locHrefUtil.getLocHref(), 'init');
    };

    const dealXgId = offerSessionUtils.getDealXgId();
    const bffApiUrlResult = getBffApiUrl(appPrefix, environment);

    if (dealXgId) {
        const commonOrgId = offerSessionUtils.getCommonOrgId();
        const queryString = commonOrgId ? `?orgId=${commonOrgId}` : '';
        const base = `${bffApiUrlResult.url}/dealXg/${dealXgId}${queryString}`;

        endpoint = base;
    } else if (environment !== HostEnvironment.None && coOrgId) {
        // Create a new deal
        const base = `${bffApiUrlResult.url}/orgId/${coOrgId}`;
        endpoint = base;
    } else {
        // if we fall here it means we do not have the right values
        return {
            type: applicationLoadErrorActionTypes.APPLICATION_LOAD_ERROR,
            payload: {
                message: `"${DEAL_EXCHANGE_ID}" is not available.`
            }
        };
    }

    if (bffApiUrlResult.environmentName !== bffApiUrlResult.defaultEnvironmentName) {
        /* eslint-disable-next-line no-console */
        console.log(
            'OVERRIDE APPLIED: ' +
                `Environment ${bffApiUrlResult.defaultEnvironmentName} --> ${bffApiUrlResult.overriddenEnvironmentName}`
        );
    }

    const environmentType = offerSessionUtils.identifyEnvironment(bffApiUrlResult.environmentName);
    offerSessionUtils.setEnvironmentType(environmentType);

    const referrer = getDocumentReferrer();

    const request: RSAACallRequest = {
        endpoint,
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'x-dr-referrer': referrer },
        types: [
            bootstrapActionTypes.BOOTSTRAP_REQUEST,
            bootstrapActionTypes.BOOTSTRAP_SUCCESS,
            {
                type: 'BOOTSTRAP_FAILURE',
                meta:
                    // this is RSAA standard code; we'll assert this coverage behavior in the reducer.
                    // istanbul ignore next
                    (_action: any, _state: any, res: any) => {
                        if (res) {
                            return {
                                status: res.status,
                                statusText: res.statusText
                            };
                        } else {
                            return {
                                statusText: 'Network request failed'
                            };
                        }
                    }
            }
        ],
        // Overriding the fetch function to ensure compatibility with the module's context.
        // When working with a federated module, the global `fetch` instance (window.fetch) may differ from the host application's instance.
        // This can cause issues with interceptors
        fetch: async (...args) => {
            return await fetch(...args);
        }
    };

    return {
        [RSAA]: request
    } as RSAACallAction;
};
